/** biome-ignore-all lint/suspicious/noArrayIndexKey: biome ignore */
'use client'
import cn from 'classnames'
import { cubicBezier } from 'motion'
import { motion } from 'motion/react'
import type { FC } from 'react'
import { IconComponent } from '@/components/Icon/Icon'
import type { Content } from '@/content-types'
import { ContentLink, RichText, type SliceComponentProps } from '@/lib/rich-text'
import { linkResolver } from '@/utils/linkResolver'

/**
 * Props for `Promo`.
 */
export type PromoProps = SliceComponentProps<Content.PromoSlice>

/**
 * Component for "Promo" Slices.
 */
const Promo: FC<PromoProps> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={cn(
        'bg-ink relative flex flex-col items-start gap-8 overflow-hidden border-b border-white/10 py-20 md:gap-2.5 md:space-y-0 md:py-28'
      )}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-72 left-1/2 h-[640px] w-[1000px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(129,12,168,0.55),transparent)] blur-2xl"
      />
      <div className="relative container flex flex-col items-center justify-center gap-3">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{
            ease: cubicBezier(0.3, 0.3, 0.3, 0.4),
            duration: 0.6,
          }}
        >
          <RichText
            field={slice.primary.title}
            components={{
              heading1: ({ children }) => <h1 className="text-center text-3xl font-bold">{children}</h1>,
              heading2: ({ children }) => <h2 className="text-center text-2xl font-semibold">{children}</h2>,
              heading3: ({ children }) => (
                <h3 className="font-rubik max-w-[20ch] text-center text-[36px] leading-[1.05] font-semibold tracking-tight text-white md:text-[56px]">
                  {children}
                </h3>
              ),
              paragraph: ({ children }) => <p className="text-center text-lg">{children}</p>,
            }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{
            ease: cubicBezier(0.3, 0.3, 0.3, 0.4),
            duration: 0.6,
            delay: 0.075,
          }}
        >
          <RichText
            field={slice.primary.text}
            components={{
              heading5: ({ children }) => (
                <h5 className="font-rubik text-center text-2xl leading-7 font-light text-white">
                  {children}
                </h5>
              ),
              paragraph: ({ children }) => (
                <p className="mx-auto mt-3 max-w-[60ch] text-center text-[18px] leading-7 text-white/70 md:text-[20px] md:leading-8">
                  {children}
                </p>
              ),
            }}
          />
        </motion.div>

        {slice.variation === 'moreButtons' && Array.isArray(slice.primary.button) ? (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.6,
              scale: { type: 'spring', visualDuration: 0.4, bounce: 0.5 },
              delay: 0.15,
            }}
            className="flex flex-col items-center gap-4 md:flex-row md:justify-center"
          >
            {slice.primary.button.map((button, idx) => (
              <ContentLink
                key={idx}
                field={button}
                linkResolver={linkResolver}
                className={cn(
                  'mt-4 mb-2 inline-flex items-center justify-center px-12 py-2.5 text-center text-lg font-semibold transition-all duration-200 ease-in-out',
                  button.variant === 'Contained' &&
                    'bg-primary hover:bg-signal rounded-full text-white shadow-[0_12px_40px_-10px_rgba(193,71,233,0.7)]',
                  button.variant === 'Text' && 'gap-2 text-white'
                )}
              >
                {button.variant === 'Text' && (
                  <IconComponent icon="ChevronRight" size="xsmall" className="text-secondary mt-0.5" />
                )}
                {button?.text}
              </ContentLink>
            ))}
          </motion.div>
        ) : slice.primary.button && !Array.isArray(slice.primary.button) ? (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.6,
              scale: { type: 'spring', visualDuration: 0.4, bounce: 0.5 },
              delay: 0.15,
            }}
          >
            <ContentLink
              field={slice.primary.button}
              linkResolver={linkResolver}
              className="bg-primary hover:bg-signal mt-4 mb-2 inline-block rounded-full px-12 py-3 text-center leading-7 font-semibold text-white shadow-[0_12px_40px_-10px_rgba(193,71,233,0.7)] transition md:text-lg"
            >
              {slice.primary.button?.text}
            </ContentLink>
          </motion.div>
        ) : null}
      </div>
    </section>
  )
}

export default Promo
